import { useEffect, useState } from 'react'
import { useAccountEnv, isApiError } from '../client'
import type { PortalSubscription, PortalPermit, PaymentMethod } from '../types'
import { SecLbl, ErrorNote, mapsUrl, monthlyPrice, renewalDate, statusLabel, formatCardBrand } from '../primitives'
import { ChevR } from '../icons'

// Green ACTIVE pill (self-managed) or gray MANAGED pill (corporate/read-only).
function PermitBadge({ text, tone }: { text: string; tone: 'active' | 'attention' | 'managed' | 'onDark' }) {
  // On the purple card, badges sit on a dark ground: use an opaque white pill so
  // the colored label reads (mirrors ActiveSessionCard's on-purple overlays). On
  // light cards, the managed pill uses the neutral gray tokens.
  const styles: Record<typeof tone, { bg: string; fg: string }> = {
    active:    { bg: 'var(--color-success-bg)',  fg: 'var(--color-success)' },
    attention: { bg: 'rgba(255,255,255,0.92)',   fg: 'var(--color-error)' },
    managed:   { bg: 'var(--color-gray-200)',    fg: 'var(--color-gray-700)' },
    onDark:    { bg: 'rgba(255,255,255,0.92)',   fg: 'var(--color-success)' },
  }
  const s = styles[tone]
  return (
    <span style={{
      fontSize: 10, fontWeight: 800, letterSpacing: 0.3, textTransform: 'uppercase',
      background: s.bg, color: s.fg, padding: '3px 9px', borderRadius: 999, whiteSpace: 'nowrap',
    }}>{text}</span>
  )
}

function GetDirectionsLink({ zone, onDark }: { zone: { name: string; address: string | null }; onDark?: boolean }) {
  if (!zone.address) return null
  return (
    <a href={mapsUrl(zone.address)} target="_blank" rel="noopener noreferrer" style={{
      display: 'block', fontWeight: 700, fontSize: 12, textDecoration: 'underline',
      color: onDark ? 'var(--color-white)' : 'var(--color-brand)', opacity: onDark ? 0.85 : 1,
    }}>Get directions</a>
  )
}

// Tappable summary card in the Permits list.
function SubSummaryCard({ sub, onOpen }: { sub: PortalSubscription; onOpen: () => void }) {
  const managed = sub.managed
  return (
    <button type="button" onClick={onOpen} style={{
      width: '100%', textAlign: 'left', display: 'block', marginBottom: 12,
      borderRadius: 'var(--radius-md)', padding: '17px 18px',
      background: managed ? 'var(--color-white)' : 'var(--color-brand)',
      color: managed ? 'var(--color-black)' : 'var(--color-white)',
      border: managed ? '1px solid var(--color-gray-300)' : 'none',
      borderLeft: managed ? '4px solid var(--color-brand)' : undefined,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 16, fontWeight: 800 }}>Zone {sub.zone.name}</span>
        {managed
          ? <PermitBadge text="Managed" tone="managed" />
          : <PermitBadge text={statusLabel(sub.status)} tone={sub.status === 'past_due' ? 'attention' : 'onDark'} />}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.4 }}>
            {monthlyPrice(sub.amountCents)}
            <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: 'normal', opacity: managed ? 1 : 0.85, color: managed ? 'var(--color-gray-600)' : undefined }}>/mo</span>
          </div>
          <div style={{ fontSize: 12, fontWeight: 500, marginTop: 4, opacity: managed ? 1 : 0.9, color: managed ? 'var(--color-gray-600)' : undefined }}>
            {managed ? `Paid by ${managed.orgName}` : 'Subscription'} · Plate {sub.plate}
          </div>
        </div>
        <span style={{ flexShrink: 0, opacity: managed ? 1 : 0.85, color: managed ? 'var(--color-gray-600)' : 'var(--color-white)' }}><ChevR /></span>
      </div>
    </button>
  )
}

// One-off (non-billed) authorization — read-only, no detail view.
function OneOffPermitCard({ permit }: { permit: PortalPermit }) {
  const zoneNames = permit.zones.map(z => `Zone ${z.name}`).join(', ')
  return (
    <div style={{
      background: 'var(--color-white)', border: '1px solid var(--color-gray-300)',
      borderLeft: '4px solid var(--color-brand)', borderRadius: 'var(--radius-md)',
      padding: '15px 17px', marginBottom: 12,
    }}>
      <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--color-black)', marginBottom: 3 }}>{permit.label}</div>
      <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-gray-600)' }}>
        {zoneNames || 'Permit'} · {permit.plates.length > 1 ? 'Plates' : 'Plate'} {permit.plates.join(', ')}
      </div>
      {permit.validUntil && (
        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-gray-600)', marginTop: 3 }}>
          Valid through {renewalDate(permit.validUntil)}
        </div>
      )}
      {permit.zones[0]?.address && <div style={{ marginTop: 8 }}><GetDirectionsLink zone={permit.zones[0]} /></div>}
    </div>
  )
}

// Detail view. Self-managed shows renewal + billing + the auto-renew toggle and
// cancel-confirm flow (⚠️ money — mutates the live subscription via the Worker,
// which re-checks ownership); corporate/managed seats stay strictly read-only.
function PermitDetail({ sub, onBack, onUpdated }: {
  sub: PortalSubscription
  onBack: () => void
  onUpdated: (s: PortalSubscription) => void
}) {
  const { client, billingPortalEnabled } = useAccountEnv()
  const managed = sub.managed
  const [card, setCard] = useState<PaymentMethod | null>(null)
  const [openingBilling, setOpeningBilling] = useState(false)
  const [cancelConfirm, setCancelConfirm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [toggleError, setToggleError] = useState<string | null>(null)

  // Apply a live auto-renew change. autoRenew=false = cancel at period end (the
  // pass stays active until currentPeriodEnd). The Worker is authoritative; we
  // reflect its returned record so the toggle can't drift from real state.
  const applyAutoRenew = async (next: boolean) => {
    if (saving) return
    setSaving(true)
    setToggleError(null)
    try {
      const updated = await client.setAutoRenew(sub.id, next)
      onUpdated(updated)
      setCancelConfirm(false)
    } catch (e) {
      setToggleError(isApiError(e) ? e.message : 'We couldn’t update your subscription right now. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    if (managed) return  // corporate seats: the company owns billing, not the employee
    let cancelled = false
    void (async () => {
      try {
        const pm = await client.fetchPaymentMethod()
        if (!cancelled) setCard(pm)
      } catch { /* display-only */ }
    })()
    return () => { cancelled = true }
  }, [managed])

  const openBilling = async () => {
    if (openingBilling) return
    setOpeningBilling(true)
    try {
      const { url } = await client.createBillingSession()
      window.location.href = url
    } catch { setOpeningBilling(false) }
  }

  const backBtn = (
    <button type="button" onClick={onBack} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--color-brand)',
      fontWeight: 700, fontSize: 13, marginBottom: 14,
    }}>
      <span style={{ transform: 'scaleX(-1)', display: 'inline-flex' }}><ChevR /></span> Back
    </button>
  )

  if (managed) {
    return (
      <div className="pka-fadein">
        {backBtn}
        <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--color-black)', marginBottom: 14 }}>Corporate permit</div>
        <div style={{
          background: 'var(--color-white)', border: '1px solid var(--color-gray-300)',
          borderLeft: '4px solid var(--color-brand)', borderRadius: 'var(--radius-md)', padding: '17px 18px', marginBottom: 12,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: 'var(--color-black)' }}>Zone {sub.zone.name}</span>
            <PermitBadge text="Managed" tone="managed" />
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.4, color: 'var(--color-black)' }}>
            {monthlyPrice(sub.amountCents)}<span style={{ fontSize: 14, fontWeight: 600, letterSpacing: 'normal', color: 'var(--color-gray-600)' }}>/mo</span>
          </div>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-gray-600)', marginTop: 4, marginBottom: 8 }}>
            Subscription · Plate {sub.plate}
          </div>
          <GetDirectionsLink zone={sub.zone} />
          <div style={{ background: 'var(--color-gray-100)', borderRadius: 10, padding: '10px 14px', marginTop: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-gray-600)', letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 2 }}>Managed by</div>
            <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--color-black)' }}>{managed.orgName}</div>
          </div>
        </div>
        <div style={{
          background: 'var(--color-brand-light)', border: '1px solid var(--color-gray-300)',
          borderRadius: 'var(--radius-md)', padding: '13px 15px',
        }}>
          <div style={{ fontSize: 13, color: 'var(--color-black)', fontWeight: 500, lineHeight: 1.55 }}>
            Billing, renewal, and cancellation for this permit are handled by your company.
            Reach out to your admin at {managed.orgName} for any changes.
          </div>
        </div>
      </div>
    )
  }

  const renewing = !sub.cancelAtPeriodEnd && sub.status !== 'canceled'

  // Cancel confirmation — turning auto-renew OFF routes here first.
  if (cancelConfirm) {
    return (
      <div className="pka-fadein">
        {backBtn}
        <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--color-black)', marginBottom: 8 }}>Turn off auto-renewal?</div>
        <div style={{ fontSize: 13, color: 'var(--color-gray-600)', fontWeight: 500, lineHeight: 1.55, marginBottom: 20 }}>
          {sub.currentPeriodEnd
            ? <>Your subscription stays active through <span style={{ fontWeight: 700, color: 'var(--color-black)' }}>{renewalDate(sub.currentPeriodEnd)}</span>. After that it won’t renew and you won’t be charged again — you’ll just pay per session at Zone {sub.zone.name}.</>
            : <>Your subscription won’t renew and you won’t be charged again — you’ll just pay per session at Zone {sub.zone.name}.</>}
        </div>
        {toggleError && <div style={{ marginBottom: 12 }}><ErrorNote>{toggleError}</ErrorNote></div>}
        <button type="button" onClick={() => void applyAutoRenew(false)} disabled={saving} style={{
          width: '100%', background: '#FDECEC', border: '1.5px solid #F4C7C4', color: '#B42318',
          borderRadius: 13, padding: 14, fontSize: 15, fontWeight: 700, marginBottom: 10,
          opacity: saving ? 0.6 : 1, cursor: saving ? 'default' : 'pointer',
        }}>{saving ? 'Turning off…' : 'Yes, turn off auto-renewal'}</button>
        <button type="button" onClick={() => { setCancelConfirm(false); setToggleError(null) }} disabled={saving} style={{
          width: '100%', background: 'var(--color-brand)', border: 'none', color: 'var(--color-white)',
          borderRadius: 13, padding: 14, fontSize: 15, fontWeight: 700, cursor: saving ? 'default' : 'pointer',
        }}>Keep auto-renewal on</button>
      </div>
    )
  }

  return (
    <div className="pka-fadein">
      {backBtn}
      <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--color-black)', marginBottom: 14 }}>Subscription</div>
      <div style={{ background: 'var(--color-brand)', borderRadius: 'var(--radius-md)', padding: '17px 18px', color: 'var(--color-white)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 800 }}>Zone {sub.zone.name}</span>
          <PermitBadge text={statusLabel(sub.status)} tone={sub.status === 'past_due' ? 'attention' : 'onDark'} />
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -0.4 }}>
          {monthlyPrice(sub.amountCents)}<span style={{ fontSize: 14, fontWeight: 600, letterSpacing: 'normal', opacity: 0.85 }}>/mo</span>
        </div>
        <div style={{ fontSize: 12, opacity: 0.9, fontWeight: 500, marginTop: 4, marginBottom: 8 }}>Subscription · Plate {sub.plate}</div>
        <GetDirectionsLink zone={sub.zone} onDark />
        {sub.currentPeriodEnd && (
          <div style={{
            background: 'rgba(255,255,255,0.15)', borderRadius: 10, padding: '10px 14px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginTop: 14,
          }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.8, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 2 }}>
                {renewing ? 'Next renewal' : 'Access ends'}
              </div>
              <div style={{ fontSize: 14, fontWeight: 800 }}>{renewalDate(sub.currentPeriodEnd)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 10, fontWeight: 700, opacity: 0.8, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 2 }}>Billing</div>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: card?.last4 || !billingPortalEnabled ? 3 : 0 }}>
                {card?.last4 ? `${formatCardBrand(card.brand)} •••• ${card.last4}` : 'Saved card'}
              </div>
              {billingPortalEnabled && (
                <button type="button" onClick={openBilling} disabled={openingBilling} style={{
                  color: 'var(--color-white)', opacity: 0.85, fontWeight: 700, fontSize: 11, textDecoration: 'underline',
                }}>{openingBilling ? 'Opening…' : 'Update'}</button>
              )}
            </div>
          </div>
        )}
      </div>
      {/* Auto-renewal toggle — ON→OFF opens the confirm step; OFF→ON resumes live. */}
      <div style={{
        background: 'var(--color-white)', border: '1px solid var(--color-gray-300)', borderRadius: 13,
        padding: '13px 15px', marginTop: 8, display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-black)' }}>Auto-renewal</div>
          <div style={{ fontSize: 12, color: 'var(--color-gray-600)', fontWeight: 500, marginTop: 1 }}>
            {renewing
              ? (sub.currentPeriodEnd ? `Renews automatically on ${renewalDate(sub.currentPeriodEnd)}` : 'Renews automatically each month')
              : (sub.currentPeriodEnd ? `Ends ${renewalDate(sub.currentPeriodEnd)} — will not renew` : 'Will not renew')}
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={renewing}
          aria-label="Auto-renewal"
          // Defense-in-depth: a fully-ended pass can't be resumed (the read path
          // already omits `canceled`, so this only guards a stale/edge record).
          disabled={saving || sub.status === 'canceled'}
          onClick={() => { if (renewing) { setToggleError(null); setCancelConfirm(true) } else void applyAutoRenew(true) }}
          style={{
            width: 42, height: 24, borderRadius: 12, flexShrink: 0, position: 'relative',
            background: renewing ? 'var(--color-brand)' : 'var(--color-gray-400, #C8C8C8)',
            border: 'none', transition: 'background .2s',
            cursor: (saving || sub.status === 'canceled') ? 'default' : 'pointer',
            opacity: (saving || sub.status === 'canceled') ? 0.6 : 1,
          }}
        >
          <div style={{
            position: 'absolute', top: 2, left: renewing ? 20 : 2, width: 20, height: 20, borderRadius: '50%',
            background: 'var(--color-white)', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
          }} />
        </button>
      </div>
      {toggleError && !cancelConfirm && <div style={{ marginTop: 10 }}><ErrorNote>{toggleError}</ErrorNote></div>}
      {!renewing && (
        <div style={{ fontSize: 12, color: 'var(--color-gray-600)', fontWeight: 500, lineHeight: 1.55, marginTop: 10 }}>
          {sub.currentPeriodEnd
            ? `Your pass stays active through ${renewalDate(sub.currentPeriodEnd)} and will not renew after that.`
            : 'Your pass will not renew.'}
        </div>
      )}
    </div>
  )
}

export function PermitsSection() {
  const { client } = useAccountEnv()
  const [subs, setSubs] = useState<PortalSubscription[] | null>(null)
  const [permits, setPermits] = useState<PortalPermit[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [detail, setDetail] = useState<PortalSubscription | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const [s, p] = await Promise.all([client.fetchSubscriptions(), client.fetchPermits()])
        if (!cancelled) { setSubs(s); setPermits(p) }
      } catch (e) {
        if (!cancelled) setError(isApiError(e) ? e.message : 'We couldn’t load your permits right now.')
      }
    })()
    return () => { cancelled = true }
  }, [])

  if (error) return <ErrorNote>{error}</ErrorNote>

  if (subs === null || permits === null) {
    return (
      <div style={{ padding: '28px 0', textAlign: 'center' }}>
        <div style={{
          width: 26, height: 26, border: '3px solid var(--color-gray-300)', borderTopColor: 'var(--color-brand)',
          borderRadius: '50%', margin: '0 auto', animation: 'pka-spin 0.8s linear infinite',
        }} />
      </div>
    )
  }

  // Reflect a live auto-renew change in both the open detail and the list card,
  // so state stays consistent without a full refetch.
  const handleUpdated = (updated: PortalSubscription) => {
    setDetail(updated)
    setSubs(prev => prev ? prev.map(s => s.id === updated.id ? updated : s) : prev)
  }

  if (detail) return <PermitDetail sub={detail} onBack={() => setDetail(null)} onUpdated={handleUpdated} />

  const personal = subs.filter(s => !s.managed)
  const corporate = subs.filter(s => s.managed)

  if (subs.length === 0 && permits.length === 0) {
    return (
      <div style={{
        background: 'var(--color-gray-100)', border: '1.5px dashed var(--pk-brand-mid)',
        borderRadius: 'var(--radius-md)', padding: 18, textAlign: 'center',
      }}>
        {/* Covers BOTH buckets this tab renders, which are different products:
            a subscription is driver-bought and renews; a permit is issued by a
            building/employer and runs to a fixed date. Naming only one — or
            telling someone to "buy one at checkout" when what they need is a
            permit — sends them down a path that doesn't exist. */}
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--color-black)', marginBottom: 3 }}>No subscriptions or permits yet</div>
        <div style={{ fontSize: 12, color: 'var(--color-gray-600)', fontWeight: 500 }}>
          Buy a subscription at any participating lot’s checkout. Permits are issued by your building or employer.
        </div>
      </div>
    )
  }

  return (
    <>
      {personal.length > 0 && (
        <>
          <SecLbl>{personal.length > 1 ? 'Self-managed subscriptions' : 'Self-managed subscription'}</SecLbl>
          {personal.map(s => <SubSummaryCard key={s.id} sub={s} onOpen={() => setDetail(s)} />)}
        </>
      )}
      {corporate.length > 0 && (
        <>
          <SecLbl>{corporate.length > 1 ? 'Corporate permits' : 'Corporate permit'}</SecLbl>
          {corporate.map(s => <SubSummaryCard key={s.id} sub={s} onOpen={() => setDetail(s)} />)}
        </>
      )}
      {permits.length > 0 && (
        <>
          <SecLbl>Permits</SecLbl>
          {permits.map(p => <OneOffPermitCard key={p.id} permit={p} />)}
        </>
      )}
    </>
  )
}
